import { Injectable, OnDestroy, Injector } from '@angular/core';
import { distinctUntilChanged, BehaviorSubject, Observable, filter, takeUntil, shareReplay } from 'rxjs';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarVerticalPosition, MatSnackBarHorizontalPosition } from '@angular/material/snack-bar';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { Subject } from 'rxjs';
import { ImageFullSizeComponent } from '../dialogs/image-full-size/image-full-size.component';
import { MatDialog } from '@angular/material/dialog';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { ConfirmDialogComponent } from 'src/app/dialogs/confirm-dialog/confirm-dialog.component';
import { formatDate } from '@angular/common';
import { environment } from '@environments/environment';
import { ParamMap } from '@angular/router';

/** Default App Name */
export const defAppName: string = `<span style="font-weight: bold;">PRISM</span><span style="color: blue;">PdM</span> Technology Platform`;
//'PRISMPdM Maintenance Platform';

/** Image Path */
const _imagePath: string = environment.defaultImagePath;

export type QueryParamType = 'string' | 'number' | 'boolean';

/** Image Location Types */
export enum imgTypes {
	accounts = 1,
	organisations = 2,
	organisationassets = 3,
	sublocations = 4,
	sensors = 5,
	datapoints = 6
}

/** Image Error Path */
export const ImageErrorPath = "https://stmgwebappstorage.blob.core.windows.net/prismpdm/assets/noimage.png";

/** Get Image Path */
export function imagePath(accountID?: string | "", imgType?: imgTypes | imgTypes.accounts): string {
	var rtnPath = "";
	var accountsPath = accountID != "" ? `${_imagePath}accounts/${accountID}/` : _imagePath;
	switch (imgType) {
		case imgTypes.accounts:
			rtnPath = `${_imagePath}accounts/`
			break;
		case imgTypes.organisations:
			rtnPath = `${accountsPath}organisations/`
			break;
		case imgTypes.organisationassets:
			rtnPath = `${accountsPath}organisationassets/`
			break;
		case imgTypes.sublocations:
			rtnPath = `${accountsPath}sublocations/`
			break;
		case imgTypes.sensors:
			rtnPath = `${accountsPath}sensors/`
			break;
		case imgTypes.datapoints:
			rtnPath = `${accountsPath}datapoints/`
			break;
		default:
			// return accountsPath;
			rtnPath = accountsPath;
	}
	/** Return the Path in lower case */
	return rtnPath.toLocaleLowerCase();
}

/** On Error Default Image place holder */
export const onErrorImage: string = imagePath + environment.defaultNoImage;
/** Image Account Path */
// export const imagePathAccounts: string = imagePath + "accounts/";
/** Image Organisations Path */
export const imagePathOrganisations: string = imagePath + "organisations/";
/** Image Assets Path */
export const imagePathAssets: string = imagePath + "organisationassets/";
/** Image Sub Locations Path */
export const imagePathSubLocations: string = imagePath + "sublocations/";
/** Image Sensors Path */
export const imagePathSensors: string = imagePath + "sensors/";
/** Image Datapoints Path */
export const imagePathDatapoints: string = imagePath + "datapoints/";

/**
 * convert Epoch To Date String
 * @param epoch
 * @returns
 */
export function convertEpochToDateString(epoch: number): string {
	const date = new Date(epoch);
	return formatDate(date, "dd MMMM YYYY HH:mm:ss", "en-en")
}

/**
 * makeDateSmall
 * @param date Date to format.
 * @returns Formatted date string.
 *
 * Angular date format:
 * EEE  - abbreviated weekday name (Thu)
 * EEEE - full weekday name (Thursday)
 * dd   - day of month
 * MM   - month
 * yyyy - 4-digit year
 */
export function makeDateSmall(date: Date): string {
	return formatDate(date, "EEE dd/MM/YYYY", "en-en");
}

/**
 * Interface for Default SQL Responses
 */
export interface ISQLResult {
	/** Result Description */
	Result: string;
	/** Error Description */
	Error: string;
	/** Data */
	Data: any;
}

/**
 * Snack Type for popup
 * @readonly
 * @enum {number}
 */
export enum SnackType {
	Info = 0,
	Alert = 1,
	Error = 2,
}

/**
 * CacheEntry
 */
export interface CacheEntry<T> {
	timestamp: number;
	data$: Observable<T>;
}

/** DatePickerPresets - #71 */
export const DatePickerPresets: Object[] = [
	{
		label: "Last Year",
		start: new Date(new Date(new Date(new Date(new Date().setFullYear(new Date().getFullYear() - 1)).setMonth(0)).setDate(1)).toDateString()),
		end: new Date(new Date(new Date(new Date(new Date().setFullYear(new Date().getFullYear() - 1)).setMonth(11)).setDate(31)).toDateString())
	},
	{
		label: "This Year",
		start: new Date(new Date(new Date(new Date().setMonth(0)).setDate(1)).toDateString()),
		end: new Date(new Date(new Date(new Date().setMonth(11)).setDate(31)).toDateString())
	},
	{
		label: "Last 6 Months",
		start: new Date(new Date(new Date(new Date().setMonth(new Date().getMonth() - 6)).setDate(1)).toDateString()),
		end: new Date(new Date(new Date().setDate(0)).toDateString())
	},
	{
		label: "Last Month",
		start: new Date(new Date(new Date(new Date().setMonth(new Date().getMonth() - 1)).setDate(1)).toDateString()),
		end: new Date(new Date(new Date().setDate(0)).toDateString())
	},
	{
		label: "This Month",
		start: new Date(new Date(new Date().setDate(1)).toDateString()),
		end: new Date(new Date(new Date(new Date().setMonth(new Date().getMonth() + 1)).setDate(0)).toDateString())
	},
	{
		label: "Last Week",
		start: new Date(new Date(new Date().setDate(new Date().getDate() - (7 + (new Date().getDay() - 1)))).toDateString()),
		end: new Date(new Date(new Date().setDate(new Date().getDate() - (7 - (7 - (new Date().getDay()))))).toDateString())
	},
	{
		label: "This Week",
		start: new Date(new Date(new Date().setDate(new Date().getDate() - (new Date().getDay() - 1))).toDateString()),
		end: new Date(new Date(new Date().setDate(new Date().getDate() + (7 - (new Date().getDay())))).toDateString())
	},
	{
		label: "Yesterday",
		start: new Date(new Date(new Date().setDate(new Date().getDate() - 1)).toDateString()),
		end: new Date(new Date(new Date().setDate(new Date().getDate() - 1)).toDateString())
	},
	{
		label: "Today",
		start: new Date(new Date().toDateString()),
		end: new Date(new Date().toDateString())
	}
];

/**
 * GlobalFunctions
 * @class
 */
@Injectable({
	providedIn: 'root'
})
export class GlobalFunctions implements OnDestroy {

	/** Show the Spinner Subject */
	private showSpinnerSubject: BehaviorSubject<any>;
	/** Observable Show Spinner value */
	public showSpinner$: Observable<any>;

	/** Enable Console Messages */
	private Debug: boolean = false;
	/** accessToken */
	private accessToken = "";

	/** This User ID */
	private userId = "";

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** siteSubscriptions */
	private ss: siteSubscriptions;

	/** selectedAccountGUID */
	private selectedAccountGUID: string;

	/** cache Life time */
	private readonly cacheLifetime = 1 * 60 * 1000; // 1 minutes

	/**
	 * cacheRequest
	 * @param cache 
	 * @param key 
	 * @param factory 
	 * @param [lifetimeMs=300000] 
	 * @returns 
	 */
	public cacheRequest<T>(
		cache: Map<string, CacheEntry<T>>,
		key: string,
		factory: () => Observable<T>,
		lifetimeMs = this.cacheLifetime
	): Observable<T> {

		const cached = cache.get(key);

		if (cached && Date.now() - cached.timestamp < lifetimeMs) {
			return cached.data$;
		}

		const request$ = factory().pipe(
			shareReplay({ bufferSize: 1, refCount: false })
		);

		cache.set(key, {
			timestamp: Date.now(),
			data$: request$
		});

		return request$;
	}

	/**
	 * cacheClear
	 * @param cache 
	 * @returns 
	 */
	public cacheClear<T>(
		cache: Map<string, CacheEntry<T>>
	): boolean {

		try {
			cache.clear();
			return true;
		}
		catch { return false; }

	}

	/**
	 * cacheDeleteKey
	 * @param cache 
	 * @param key 
	 * @returns 
	 */
	public cacheDeleteKey<T>(
		cache: Map<string, CacheEntry<T>>,
		key: string
	): boolean {

		try {
			const cached = cache.get(key);

			if (cached) {
				cache.delete(key)
				return true
			}
			return true;
		}
		catch { return false; }

	}

	/**
	 * Log message to the Console
	 * @param str
	 */
	consoleLog(str: any) { if (this.Debug == true) console.log(str); }

	/**
	 * Default Constructor
	 * @param snackBar MatSnackBar object
	 * @param auth AuthService
	 * @param dialog dialog
	 */
	constructor(
		private snackBar: MatSnackBar,
		public auth: AuthService,
		public dialog: MatDialog,
		private injector: Injector
	) {

		try {
			this.showSpinnerSubject = new BehaviorSubject<any>("false");
			this.showSpinner$ = this.showSpinnerSubject.asObservable();
		}
		catch (error) { console.error(error) }

		this.auth.idTokenClaims$.pipe(
			distinctUntilChanged(),
			filter(value => !!value)
		).subscribe((value: any) => {
			this.accessToken = value.__raw; // Assuming '__raw' contains the token
			// console.log(`ID Token: ${this.accessToken}`);

			// You can also access specific claims if needed
			this.userId = value.sub; // 'sub' claim contains the user ID
			// console.log(`User ID: ${userId}`);

			setTimeout(() => {
				this.getSiteSubscriptions();
			}, 0);
		});

		// this.auth.isAuthenticated$..subscribe(value =>{
		// 	console.log(`is Authenitcated: ${value}`)
		// })

		// this.auth.appState$.subscribe(value =>{
		// 	console.log(`App State: ${value}`)
		// })

		// this.auth.idTokenClaims$.subscribe(value =>{
		// 	console.log(`idTokenClaims: ${value}`)
		// })

		// this.auth.error$.subscribe(value =>{
		// 	console.log(`Error: ${value}`)
		// })

		// try {
		//     this.auth.user$.pipe(
		//         takeUntil(this.finalise), distinctUntilChanged()
		//     ).subscribe({
		//         next: (profile: any) => {
		//             if (profile) {
		// 				if (this.accessToken == ""){
		// 					this.auth.getAccessTokenSilently().subscribe((accessToken: string) => {
		// 						this.accessToken = accessToken;
		// 					});
		// 				}
		//             }
		//         }
		//     });
		// }
		// catch (error) {
		//     console.log(error);
		// }

	}

	getSiteSubscriptions() {
		this.ss = this.injector.get(siteSubscriptions);

		this.ss.selectedAccountGUID$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter(guid => !!guid)
		).subscribe({
			next: (guid: string) => {
				if (guid) {
					this.selectedAccountGUID = guid;
				}
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Format a string for Title Case
	 * @param str String to Format
	 * @returns Formatted String
	 */
	titleCase(str: string) {
		str.replace(/(^\w|\s\w)(\S*)/g, (_, m1, m2) => m1.toUpperCase() + m2.toLowerCase());
		return str;
	}

	/**
	   * Show a snack bar message on the bottom of the screen
	   * @param message Message to display
	   * @param type Message Type 'info'|'alert'|'error'
	   * @param action Action to perform
	   * @param duration (Optional) Duration (ms) the snackbar to remain  on screen (0 = permanent)
	   * @param vPosition (Optional) 'top' | 'bottom'
	   * @param hPosition (Optional) 'start' | 'center' | 'end' | 'left' | 'right'
	   */
	DisplaySnackBar(
		{ message, type = SnackType.Info, action = "close", duration = 0, vPosition = "bottom", hPosition = "center" }:
			{
				message: string,
				type?: SnackType,
				action?: string,
				duration?: number,
				vPosition?: MatSnackBarVerticalPosition,
				hPosition?: MatSnackBarHorizontalPosition
			} = {
				message: "This is a snackBar"
			},
	): void {
		/** Check a message was supplied */
		if (message != "") {
			let config = new MatSnackBarConfig()

			if (action == "" && duration == 0) { duration = 1000 }

			config.duration = duration;
			config.verticalPosition = vPosition;
			config.horizontalPosition = hPosition;


			/** Display a red snackbar */

			config.panelClass = ['grey-snackbar']
			if (type == SnackType.Alert) { config.panelClass = ['yellow-snackbar']; }
			if (type == SnackType.Error) { config.panelClass = ['red-snackbar']; }

			let msg: any = message?.toString();

			this.snackBar.open(msg, action, config);
		}
	}

	/**
	 * Edit the Location details
	 * @param imageURL
	 */
	viewImageFullSize(imageURL: string): void {

		if (imageURL != '') {
			// Display the dialog box
			const dialogRef = this.dialog.open(ImageFullSizeComponent, {
				data: imageURL,
			});

			// Update the original Location with the result
			dialogRef.afterClosed().pipe(
				filter(result => !!result)
			).subscribe(result => {
				// console.log('The dialog was closed');
			});
		}
	}

	/**
	 * Handle Error Exceptions
	 * @param error Returned error
	 * @param msg Default Error Message
	 * @param display Show error message
	 */
	HandleError(error: any, msg: string = "", display: boolean = true) {
		/** Default the Console Message */
		let consoleMSG = msg;
		/** If no message, display error */
		if (error instanceof TypeError && msg == "") {
			let err: TypeError = error;
			msg = err.message;
			consoleMSG = err.message;
		} else if (error instanceof HttpErrorResponse && msg != "") {
			let err: HttpErrorResponse = error;
			msg = err.statusText + " | " + msg;
			consoleMSG = msg + "\r\n`" + err.error['detail'] + "`";
		} else if (msg == "") { msg = "Unhandled Error"; }

		/** Raise the error to the console */
		if (consoleMSG != "") { console.error(consoleMSG); }

		/** Display the error to the user */
		if (display) { this.DisplaySnackBar({ message: msg, type: SnackType.Error, action: "Close" }); }

		/** If there isn an error Hide the Spinner */
		this.displaySpinner(false);

	}

	/**
	 * Execute the API URL as GET, POST, PUT or DELETE
	 * @param url
	 * @param method
	 * @param data
	 * @returns
	 */
	public fetchAPI(http: HttpClient, url: string, method: string, data: any, options: any = {}): Observable<any> {

		try {
			let headerConfig = new HttpHeaders({
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': '*',
				'Authorization': `Bearer ${this.accessToken}`,
				'UserId': this.userId,
				'SelectedAccountGUID': this.selectedAccountGUID ?? ''
			});
			if (options.noContentType) {
				headerConfig = headerConfig.delete('Content-Type');
			}
			const httpOptions = {
				headers: headerConfig,
				...(options.responseType && { "responseType": options.responseType }), // Set default response if supplied
				//responseType: options.responseType || 'json'
			}

			this.consoleLog("Calling:" + url);

			if (method == "GET") {
				// Convert the JSON Parameters to HTTP URL
				let httpParams = new HttpParams();
				for (const key in data) {
					httpParams = httpParams.set(key.toString(), data[key].toString());
				}
				if (httpParams.toString() != '') {
					url = url + "?" + httpParams.toString();
				}
				return http.get<any>(url, httpOptions);
			} else if (method == "POST") {
				// console.log(url)
				// console.log(data)
				return http.post<any>(url, data, httpOptions);
			} else if (method == "PUT") {
				return http.put<any>(url, data, httpOptions);
			} else if (method == "PATCH") {
				return http.patch<any>(url, data, httpOptions);
			} else if (method == "DELETE") {
				const httpOptions = {
					headers: new HttpHeaders({
						'Content-Type': 'application/json',
						'Access-Control-Allow-Origin': '*',
						'Authorization': `Bearer ${this.accessToken}`,
						'UserId': this.userId,
						'SelectedAccountGUID': this.selectedAccountGUID ?? ''
					}),
					body: data // JSON data in the request body
				};
				return http.delete<any>(url, httpOptions);
			} else {
				return new Observable<void>();
			}

		} catch (e) { this.consoleLog(e); }
		return new Observable<void>();

	}

	/**
	 * Execute the API URL as GET, POST, PUT or DELETE
	 * @param url
	 * @param method
	 * @param data
	 * @returns
	 */
	public putForm(http: HttpClient, url: string, method: string, data: FormData, options: any = {}): Observable<any> {

		try {
			const httpOptions = {
				headers: new HttpHeaders({
					'Access-Control-Allow-Origin': '*',
					'Authorization': `Bearer ${this.accessToken}`,
					'UserId': this.userId,
					'SelectedAccountGUID': data.get("accountGUID")?.toString() ?? ''
				}),
				...(options.responseType && { "responseType": options.responseType }), // Set default response if supplied
				//responseType: options.responseType || 'json'
			}

			this.consoleLog("Calling:" + url);

			if (method == "PUT") {
				return http.put(url, data, httpOptions);
				// return http.put(url, data);
			} else {
				return new Observable<void>();
			}

		} catch (e) { this.consoleLog(e); }
		return new Observable<void>();

	}

	/**
	 * Execute the API URL as GET, POST, PUT or DELETE
	 * @param http
	 * @param url
	 * @param apiKey
	 * @param method
	 * @param data
	 * @param options
	 * @returns
	 */
	public fetchNeuronSensorAPI(http: HttpClient, url: string, apiKey: string, method: string, data: any, options: any = {}): Observable<any> {

		try {
			const httpOptions = {
				headers: new HttpHeaders({
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': '*',
					'accept': 'application/json',
					'ApiKey': `${apiKey}`
				}),
			}

			this.consoleLog("Calling:" + url);

			if (method == "GET") {
				// Convert the JSON Parameters to HTTP URL
				let httpParams = new HttpParams();
				for (const key in data) {
					httpParams = httpParams.set(key.toString(), data[key].toString());
				}
				if (httpParams.toString() != '') {
					url = url + "?" + httpParams.toString();
				}
				return http.get<any>(url, httpOptions);
			} else if (method == "POST") {
				// console.log(url)
				// console.log(data)
				return http.post<any>(url, data, httpOptions);
			} else if (method == "PUT") {
				return http.put<any>(url, data, httpOptions);
			} else if (method == "PATCH") {
				return http.patch<any>(url, data, httpOptions);
			} else if (method == "DELETE") {
				const httpOptions = {
					headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
					body: data // JSON data in the request body
				};
				return http.delete<any>(url, httpOptions);
			} else {
				return new Observable<void>();
			}

		} catch (e) { this.consoleLog(e); }
		return new Observable<void>();
	}


	/**
	 * Display the Spinner GIF
	 * @param display
	 */
	displaySpinner(display: boolean) {
		try {
			this.showSpinnerSubject.next(display);
		}
		catch (error) { }
	}

	/**
	 * User Confirmation box
	 * @param message Message to Display
	 * @param title Title to display
	 * @param confirmButton Text for Confirm Button
	 * @param cancelButton Text for Cancel Button
	 * @param width Message Box Width
	 * @returns 
	 */
	UserConfirm$(message: string, title?: string, confirmButton?: string | null, cancelButton?: string | null, width: string = '400px'): Observable<boolean> {

		confirmButton = confirmButton === undefined ? 'OK' : confirmButton; // default only if undefined 
		cancelButton = cancelButton === undefined ? 'Cancel' : cancelButton; // default only if undefined 

		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			width,
			data: { message, title, confirmButton, cancelButton }
		});

		return dialogRef.afterClosed();
	}

	/**
	 * queryParamsToObject
	 * @param queryParamMap 
	 * @param types 
	 * @returns 
	 */
	queryParamsToObject<T extends object>(
		queryParamMap: ParamMap,
		types: Record<keyof T, QueryParamType>
	): Partial<T> {
		const result: Partial<T> = {};

		queryParamMap.keys.forEach(key => {
			if (!(key in types)) {
				return;
			}

			const value = queryParamMap.get(key);

			if (value === null) {
				return;
			}

			const type = types[key as keyof T];

			switch (type) {
				case 'boolean':
					result[key as keyof T] = (value === 'true') as T[keyof T];
					break;

				case 'number':
					result[key as keyof T] = Number(value) as T[keyof T];
					break;

				default:
					result[key as keyof T] = value as T[keyof T];
					break;
			}
		});

		return result;
	}


} /** Class End */
